import React from 'react';
import { motion } from 'framer-motion';

const MissionVision = () => {
  const pillars = [
    {
      icon: (
        <svg style={{ color: '#d9a79c' }} className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      title: "Empower & Educate",
      description: "Providing knowledge and tools to help women take control of their wellness journey with confidence."
    },
    {
      icon: (
        <svg style={{ color: '#d9a79c' }} className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Safe Solutions",
      description: "Offering allergy-friendly recipes and wellness practices that honor your body's unique needs."
    },
    {
      icon: (
        <svg style={{ color: '#d9a79c' }} className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Inspire & Connect",
      description: "Building a supportive community where women inspire each other and share their healing journeys."
    },
    {
      icon: (
        <svg style={{ color: '#d9a79c' }} className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: "Holistic Wellness",
      description: "Addressing the whole person - mind, body, and spirit - for complete health transformation."
    }
  ];

  return (
    <section id="mission" className="relative py-20" style={{ backgroundColor: '#F5EBDD' }}>
      {/* Organic top curve */}
      <div className="absolute top-0 left-0 w-full overflow-hidden">
        <svg className="relative block w-full h-24" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,0 C300,80 900,80 1200,0 L1200,120 L0,120 Z" fill="#ffffff"></path>
        </svg>
      </div>

      <div className="relative pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alera's Mission - Single Unified Statement */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <div className="text-center mb-8">
              <h2 className="text-4xl md:text-5xl font-playfair font-light text-charcoal-900 mb-4">
                Alera's Mission
              </h2>
              <p className="text-sm uppercase tracking-widest text-charcoal-500 font-nunito">
                A New Era of Living with Allergies
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto rounded-3xl p-10 md:p-12 shadow-soft relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FDF9F5 0%, #F9F3F0 50%, #F5F1ED 100%)' }}>
              {/* Decorative accent blob */}
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #e8d5d0 0%, #d9c3cc 100%)' }}></div>
              
              <div className="relative z-10">
                <p className="text-xl text-charcoal-800 leading-relaxed font-nunito mb-6">
                  I created Alera after years of struggling with eczema, asthma, and food sensitivities that made everyday choices overwhelming. This is the wellness space I wish I had when I felt alone in my journey.
                </p>
                <p className="text-xl text-charcoal-800 leading-relaxed font-nunito mb-6">
                  Alera provides a safe and inspiring online space for women to find allergy-friendly recipes, holistic health tips, and accessible fitness programs. Through education, community, and personal story, we help you turn health struggles into strength and confidence.
                </p>
                <p className="text-xl text-charcoal-800 leading-relaxed font-nunito">
                  This is about more than food and workouts — it's about finding freedom, strength, and clarity in a body that feels safe and supported. We're creating a world where living with allergies feels empowered and full of possibility, not restriction.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Core Pillars */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-playfair font-light text-charcoal-900 mb-6">
              Our Goals
            </h3>
            <p className="text-xl text-charcoal-600 max-w-3xl mx-auto leading-relaxed font-nunito">
              Everything we do is built on these foundational principles
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="text-center p-8 rounded-3xl shadow-soft hover:shadow-soft-lg transition-all duration-500 bg-white/90 backdrop-blur-sm border border-slate-100/50"
              >
                <div className="flex justify-center mb-6">{pillar.icon}</div>
                <h4 className="text-xl font-playfair font-semibold text-charcoal-900 mb-4 tracking-tight">
                  {pillar.title}
                </h4>
                <p className="text-charcoal-600 leading-relaxed font-nunito">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Organic bottom curve */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg className="relative block w-full h-24" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,120 C300,40 900,40 1200,120 L1200,0 L0,0 Z" fill="#ffffff"></path>
        </svg>
      </div>
    </section>
  );
};

export default MissionVision;
